import { useState } from 'react';
import {
  channelTypeConfigs,
  channelContent,
  canalRFWStats,
  canalRFWRules,
  canalLocations,
  getContentByChannel,
  getFeaturedContent,
  getChannelDistribution,
} from '@/mocks/rfwCanalRFW';

export default function CanalRFWSection() {
  const [activeTab, setActiveTab] = useState<'canales' | 'contenido' | 'ubicaciones' | 'reglas'>('canales');
  const [selectedChannel, setSelectedChannel] = useState<string>('all');

  const featuredContent = getFeaturedContent().slice(0, 6);
  const channelDistribution = getChannelDistribution();
  const filteredContent = selectedChannel === 'all'
    ? channelContent.slice(0, 8)
    : getContentByChannel(selectedChannel).slice(0, 8);

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-rose-100 via-violet-100 to-amber-100 border border-rose-200 mb-4">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
            <span className="text-xs font-semibold text-rose-700 tracking-wide">CANAL RFW</span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Contenido <span className="bg-gradient-to-r from-rose-500 via-violet-500 to-amber-500 bg-clip-text text-transparent">oficial</span> de RFW
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
            RFW no solo conecta radios. También crea su propio contenido para que nunca te quedes sin nada que escuchar, ver o descubrir.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-3 mb-8">
          {[
            { value: canalRFWStats.totalContent.toString(), label: 'Contenidos', icon: 'ri-stack-line', color: 'text-rose-600', bg: 'bg-rose-50' },
            { value: canalRFWStats.channelsActive.toString(), label: 'Canales', icon: 'ri-radio-line', color: 'text-violet-600', bg: 'bg-violet-50' },
            { value: canalRFWStats.liveNow.toString(), label: 'En Vivo Ahora', icon: 'ri-live-line', color: 'text-amber-600', bg: 'bg-amber-50' },
            { value: canalRFWStats.exclusiveContent.toString(), label: 'Exclusivos', icon: 'ri-star-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { value: canalRFWStats.totalPlays.toLocaleString(), label: 'Reproducciones', icon: 'ri-play-circle-line', color: 'text-orange-600', bg: 'bg-orange-50' },
          ].map((stat, i) => (
            <div key={i} className={`${stat.bg} rounded-lg p-3 text-center`}>
              <div className={`text-lg font-bold ${stat.color}`}>{stat.value}</div>
              <div className="flex items-center justify-center gap-1 text-xs text-foreground-500 mt-1">
                <i className={`${stat.icon} text-xs`}></i>
                <span>{stat.label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center mb-7">
          <div className="inline-flex bg-background-100 rounded-full p-1 border border-background-200/70">
            {(['canales', 'contenido', 'ubicaciones', 'reglas'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  activeTab === tab
                    ? 'bg-rose-500 text-white'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {tab === 'canales' && 'Canales'}
                {tab === 'contenido' && 'Contenido'}
                {tab === 'ubicaciones' && 'Ubicaciones'}
                {tab === 'reglas' && 'Reglas'}
              </button>
            ))}
          </div>
        </div>

        {/* Tab: Canales */}
        {activeTab === 'canales' && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {channelTypeConfigs.map((ch) => {
                const count = getContentByChannel(ch.id).length;
                return (
                  <div
                    key={ch.id}
                    className={`bg-gradient-to-br ${ch.gradient} rounded-xl p-5 text-white relative overflow-hidden group cursor-pointer transition-transform hover:scale-[1.02]`}
                  >
                    <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors"></div>
                    <div className="relative z-10">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                          <i className={`${ch.icon} text-xl`}></i>
                        </div>
                        <div>
                          <div className="font-bold text-base">{ch.shortLabel}</div>
                          <div className="text-xs text-white/70">{ch.label}</div>
                        </div>
                      </div>
                      <p className="text-xs text-white/80 leading-relaxed mb-3 line-clamp-2">{ch.description}</p>
                      <div className="flex items-center gap-2 text-xs text-white/70">
                        <span className="bg-white/20 px-2 py-0.5 rounded-full">{count} contenidos</span>
                        <span className="bg-white/20 px-2 py-0.5 rounded-full">{ch.contentTypes.length} formatos</span>
                      </div>
                      <div className="mt-3 text-[10px] text-white/60 italic">{ch.triggerRule}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Tab: Contenido */}
        {activeTab === 'contenido' && (
          <div>
            {/* Channel filter chips */}
            <div className="flex flex-wrap items-center justify-center gap-1.5 mb-6">
              <span className="text-xs text-foreground-500 mr-1">Canal:</span>
              <button
                onClick={() => setSelectedChannel('all')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all cursor-pointer ${
                  selectedChannel === 'all'
                    ? 'bg-rose-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-rose-100 border border-background-200/70'
                }`}
              >
                Todos ({channelContent.length})
              </button>
              {channelTypeConfigs.map((ch) => (
                <button
                  key={ch.id}
                  onClick={() => setSelectedChannel(ch.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all cursor-pointer flex items-center gap-1.5 ${
                    selectedChannel === ch.id
                      ? `${ch.color === 'rose' ? 'bg-rose-500' : ch.color === 'violet' ? 'bg-violet-500' : ch.color === 'amber' ? 'bg-amber-500' : ch.color === 'emerald' ? 'bg-emerald-500' : 'bg-orange-500'} text-white`
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  <i className={`${ch.icon} text-xs`}></i>
                  {ch.shortLabel}
                  <span className={`text-xs ${selectedChannel === ch.id ? 'text-white/70' : 'text-foreground-400'}`}>
                    {getContentByChannel(ch.id).length}
                  </span>
                </button>
              ))}
            </div>

            {/* Content Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filteredContent.map((item) => {
                const ch = channelTypeConfigs.find((c) => c.id === item.channelId);
                return (
                  <div key={item.id} className="bg-background-100 rounded-lg border border-background-200/70 hover:border-rose-200 transition-colors p-4 flex gap-3">
                    {/* Thumbnail */}
                    <div className="flex-shrink-0">
                      <div className="w-28 h-20 rounded-lg overflow-hidden relative">
                        <img src={item.thumbnailUrl} alt={item.title} className="w-full h-full object-cover" />
                        {item.isLive && (
                          <div className="absolute top-1 left-1 bg-rose-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                            EN VIVO
                          </div>
                        )}
                        {item.isExclusive && !item.isLive && (
                          <div className="absolute top-1 left-1 bg-amber-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                            EXCLUSIVO
                          </div>
                        )}
                        {item.isSponsored && (
                          <div className="absolute top-1 left-1 bg-foreground-700 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                            PATROCINADO
                          </div>
                        )}
                        <div className="absolute bottom-1 right-1 bg-black/70 text-white text-[9px] font-medium px-1.5 py-0.5 rounded-full">
                          {item.duration}
                        </div>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-gradient-to-r ${ch?.gradient ?? 'from-gray-400 to-gray-500'} text-white`}>
                          {item.type}
                        </span>
                        {item.category && (
                          <span className="text-[10px] text-foreground-400">{item.category}</span>
                        )}
                      </div>
                      <div className="font-semibold text-sm text-foreground-950 leading-snug mb-1 line-clamp-2">{item.title}</div>
                      <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-2">{item.subtitle}</p>
                      <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
                        <span className="flex items-center gap-0.5">
                          <i className="ri-play-circle-line"></i>
                          {item.playsCount.toLocaleString()}
                        </span>
                        <span>·</span>
                        <span className="flex items-center gap-0.5">
                          <i className="ri-heart-line"></i>
                          {item.likesCount.toLocaleString()}
                        </span>
                        <span>·</span>
                        <span className="flex items-center gap-0.5">
                          <i className="ri-chat-3-line"></i>
                          {item.commentsCount}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {filteredContent.length === 0 && (
              <div className="text-center py-10">
                <div className="w-16 h-16 rounded-full bg-background-200/50 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-play-list-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-sm text-foreground-500">No hay contenido en este canal ahora mismo.</p>
              </div>
            )}
          </div>
        )}

        {/* Tab: Ubicaciones */}
        {activeTab === 'ubicaciones' && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {canalLocations.map((loc) => {
                const primaryCh = channelTypeConfigs.find((c) => c.id === loc.primaryChannel);
                return (
                  <a
                    key={loc.id}
                    href={loc.path}
                    className="bg-background-100 rounded-lg border border-background-200/70 p-5 hover:border-rose-200 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-xl bg-${loc.primaryChannel === 'rfw-radio' ? 'rose' : loc.primaryChannel === 'rfw-tv' ? 'violet' : loc.primaryChannel === 'rfw-tendencias' ? 'orange' : loc.primaryChannel === 'rfw-noticias' ? 'emerald' : 'amber'}-50 flex items-center justify-center`}>
                        <i className={`${loc.icon} text-lg ${loc.primaryChannel === 'rfw-radio' ? 'text-rose-500' : loc.primaryChannel === 'rfw-tv' ? 'text-violet-500' : loc.primaryChannel === 'rfw-tendencias' ? 'text-orange-500' : loc.primaryChannel === 'rfw-noticias' ? 'text-emerald-500' : 'text-amber-500'}`}></i>
                      </div>
                      <div>
                        <div className="font-semibold text-sm text-foreground-950 group-hover:text-rose-600 transition-colors">{loc.label}</div>
                        {primaryCh && (
                          <div className={`text-[10px] font-medium ${primaryCh.textColor}`}>{primaryCh.label} principal</div>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-foreground-600 leading-relaxed">{loc.description}</p>
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Tab: Reglas */}
        {activeTab === 'reglas' && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {canalRFWRules.map((rule) => (
                <div key={rule.id} className={`bg-background-100 rounded-lg p-5 border border-background-200/70 hover:border-${rule.color}-200 transition-colors`}>
                  <div className={`w-11 h-11 rounded-xl bg-${rule.color}-50 flex items-center justify-center mb-3`}>
                    <i className={`${rule.icon} text-xl text-${rule.color}-500`}></i>
                  </div>
                  <div className="font-semibold text-sm text-foreground-950 mb-2">{rule.title}</div>
                  <div className="text-xs text-foreground-600 leading-relaxed mb-3">{rule.description}</div>
                  <div className={`text-[10px] font-medium text-${rule.color}-600 bg-${rule.color}-50 rounded-full px-2 py-0.5 inline-block`}>
                    {rule.trigger}
                  </div>
                </div>
              ))}
            </div>

            {/* Channel distribution summary */}
            <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
              <div className="text-sm font-semibold text-foreground-950 mb-3">Contenido por Canal</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {channelDistribution.map((item) => (
                  <div key={item.channelId} className="text-center bg-background-50 rounded-lg p-3">
                    <i className={`${item.config?.icon ?? 'ri-radio-line'} ${item.config?.color === 'rose' ? 'text-rose-500' : item.config?.color === 'violet' ? 'text-violet-500' : item.config?.color === 'amber' ? 'text-amber-500' : item.config?.color === 'emerald' ? 'text-emerald-500' : 'text-orange-500'} text-lg block mb-1`}></i>
                    <div className="text-lg font-bold text-foreground-950">{item.count}</div>
                    <div className="text-xs text-foreground-400">{item.config?.shortLabel ?? item.channelId}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-8">
          <a
            href="/canal-rfw"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-rose-500 via-violet-500 to-amber-500 text-white rounded-full text-sm font-semibold hover:from-rose-600 hover:via-violet-600 hover:to-amber-600 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-radio-line"></i>
            Explorar todo el Canal RFW
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>

        {/* Final Rule */}
        <div className="mt-8 text-center">
          <p className="text-xs text-foreground-400 italic">La plataforma nunca debe sentirse vacía.</p>
        </div>
      </div>
    </section>
  );
}